import React from 'react';
import { Row, Col } from 'reactstrap';
import { IconButton, Button, Checkbox, DialogActions, DialogContent, Box } from '@mui/material';
import {
  ModeEditOutline,
  DeleteOutline,
  AddCircleOutline,
  CheckCircle,
  CheckCircleOutline,
  DeleteForeverOutlined,
  ClearOutlined,
  Lock,
} from '@mui/icons-material';
import { CustomPopup } from '../../../../common/Popup/CustomPopup';

import DataTableComponent from '../../../../component/DataTable/component/DataTableComponent';
import { CustomCard } from '../../../../common/Card/CustomCard';
import { CustomSelect } from '../../../../common/CustomSelect';
import { useGetUsersTableData, useDeleteUser } from './apiHooks';
import { AddEditComponent } from './component/AddEditComponent';
import { useCustomModalToggle } from '../../../../common/Dialog/CustomModal';
import { hasPermissionHook } from '../../../../hooks/hasPermission';
import { EDIT, USERS } from '../../../../constants/permissions.constants';
import { ChangePassword } from './component/ChangePassword';

export const UserTable = () => {
  const { hasPermission } = hasPermissionHook();
  const { open, toggleCustomModal } = useCustomModalToggle();
  const { open: openPasswordModal, toggleCustomModal: passwordToggleModal } = useCustomModalToggle();
  const [userId, setUserId] = React.useState();

  const [modalData, setModalData] = React.useState({
    id: null,
    action: null,
  });
  const blankData = React.useMemo(() => {
    return { data: [], page: 0, pageSize: 100, pageTotal: 0 };
  }, []);
  const [filter, setFilter] = React.useState({
    sort_by: 'name',
    order_by: 'ASC',
    page: 0,
    pageSize: 100,
  });
  const [deleteData, setDeleteData] = React.useState({
    deleteAnchor: null,
  });

  const { data: tableData, isLoading } = useGetUsersTableData(filter);
  const { mutate: deleteMutation } = useDeleteUser(filter);

  const onFilterChange = (input) => {
    setFilter((oldFilter) => ({ ...oldFilter, ...input }));
  };

  const onDeleteItem = (e, rowData) => {
    setDeleteData({
      deleteAnchor: e.currentTarget,
      openDeletePopup: true,
      userId: rowData.id,
    });
  };

  const toggleModal = ({ action, id }) => {
    setModalData({ action, id });
    toggleCustomModal();
  };

  const columns = React.useMemo(() => {
    return [
      {
        accessor: 'name',
        id: 'name',
        Header: 'Name',
        Cell: (row) => row.value,
        sort: true,
      },
      {
        accessor: 'email',
        id: 'email',
        Header: 'Email',
        Cell: (row) => row.value,
        sort: true,
      },
      {
        accessor: 'is_active',
        id: 'is_active',
        Header: 'Is Active',
        Cell: (row) => <Checkbox checked={row.value} icon={<CheckCircleOutline />} checkedIcon={<CheckCircle />} />,
        sort: true,
        minWidth: 90,
        maxWidth: 90,
      },
      {
        accessor: 'role_name',
        id: 'role_name',
        Header: 'Role',
        Cell: (row) => row.value,
        sort: true,
      },
      {
        filterable: false,
        sortable: false,
        accessor: (row) => {
          return (
            <>
              <IconButton color='primary' onClick={() => toggleModal({ action: 'update', id: row.id })} disabled={!hasPermission(USERS, EDIT)}>
                <ModeEditOutline />
              </IconButton>
              <IconButton color='error' onClick={(e) => onDeleteItem(e, row)} disabled={!hasPermission(USERS, EDIT)}>
                <DeleteOutline />
              </IconButton>
              <IconButton
                color='secondary'
                onClick={() => {
                  passwordToggleModal();
                  setUserId(row.id);
                }}
                disabled={!hasPermission(USERS)}
              >
                <Lock />
              </IconButton>
            </>
          );
        },
        id: 'btnAction',
        Header: 'Actions',
        minWidth: 150,
        maxWidth: 150,
      },
    ];
  }, [tableData?.data]);

  const handleConfirmDeleteUserClick = () => {
    deleteMutation({ id: deleteData.userId, row_status: 'D' });
    setDeleteData({ deleteAnchor: null });
  };

  return (
    <>
      <CustomCard headerTitle='Users' sxCardContainer={{ mt: 2 }}>
        <Row className='mb-3'>
          <Col md='12' className='d-flex align-items-end justify-content-end'>
            <Button
              variant='contained'
              endIcon={<AddCircleOutline />}
              color='success'
              size='small'
              onClick={() => toggleModal({ action: 'create', id: null })}
              disabled={!hasPermission(USERS, EDIT)}
            >
              Add New User
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <DataTableComponent dataSource={tableData?.data || blankData} onFilterChange={onFilterChange} columns={columns} loading={isLoading} />
          </Col>
        </Row>
      </CustomCard>
      <CustomPopup
        anchor={deleteData.deleteAnchor}
        content={
          <DeleteUserPopupContent onConfirm={handleConfirmDeleteUserClick} onCancel={() => setDeleteData((prev) => ({ deleteAnchor: null }))} />
        }
        arrow
        placement='bottom'
        onClose={() => setDeleteData((prev) => ({ deleteAnchor: null }))}
        open={deleteData.openDeletePopup}
      />
      <AddEditComponent open={open} action={modalData.action} id={modalData.id} toggleCustomModal={toggleCustomModal} />
      <ChangePassword open={openPasswordModal} toggleCustomModal={passwordToggleModal} isAdmin={true} userId={userId} />
    </>
  );
};

const DeleteUserPopupContent = ({ onConfirm, onCancel, message = `Are you sure you want to delete this user?` }) => {
  return (
    <>
      <Box>
        <DialogContent sx={{ padding: '5px' }}>{message}</DialogContent>
        <DialogActions sx={{ padding: '5px' }}>
          <IconButton color='error' onClick={onConfirm}>
            <DeleteForeverOutlined />
          </IconButton>
          <IconButton onClick={onCancel}>
            <ClearOutlined />
          </IconButton>
        </DialogActions>
      </Box>
    </>
  );
};
